import { useEffect } from 'react'

/**
 * The chat window plus app elements that toggle the chat themselves, marked
 * with data-atlas-chat-trigger.
 */
const ATLAS_WIDGET_SELECTOR =
  '[aria-label="atlas-window"], [data-atlas-chat-trigger]'

/** Atlas only mounts this element while the chat window is open. */
const ATLAS_WINDOW_SELECTOR = '[aria-label="atlas-window"]'

function closeAtlasChatWindow() {
  if (!document.querySelector(ATLAS_WINDOW_SELECTOR)) return
  window.Atlas?.call?.('closeChatWindow')
}

/**
 * Dismisses the Atlas support chat window on outside click or Escape. Atlas
 * ships no built-in click-away behavior, but its `closeChatWindow` call
 * closes the window; the window element's presence in the DOM doubles as the
 * open check. Clicks inside the window or on marked trigger elements are
 * left to their own handlers. Listens in the capture phase so clicks
 * swallowed by other components still dismiss the chat.
 */
export function useAtlasChatDismiss() {
  useEffect(() => {
    const onPointerDown = (event: Event) => {
      const target = event.target
      if (target instanceof Element && target.closest(ATLAS_WIDGET_SELECTOR)) {
        return
      }
      closeAtlasChatWindow()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      closeAtlasChatWindow()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [])
}
